import React from 'react'
import { GetStaticProps, InferGetStaticPropsType } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useTranslation } from 'next-i18next';
import i18nextConfig from '../../../next-i18next.config';
import Head from 'next/head'
import Header from '../header'
import Footer from '../footer'
import styles from '../../styles/DigitalForest.module.css';
import Image, { ImageLoaderProps } from 'next/image';
import { IMAGE_DELIVERY, imageLoader } from '@/base/utils/dataHelper';
import { useRouter } from 'next/router';

const DigitalForest = (_props: InferGetStaticPropsType<typeof getStaticProps>) => {
  const { t } = useTranslation('common');
  const router = useRouter();

  const isLanguage = router.locale?.toUpperCase().toString()


  const baseUrl = 'https://stoneity.com';
  const currentPath = router.asPath;
  const locale = router.locale || 'en';
  const canonicalUrl = locale === 'tr'
    ? `${baseUrl}/tr${currentPath}`
    : `${baseUrl}${currentPath}`;
  const alternateLocale = locale === 'tr' ? 'en' : 'tr';
  const alternatePath = `/digital-forest`;

  return (
    <>
      <Head>
        <title>{t('seo_digital_forest_title')}</title>
        <meta name="description" content={t('seo_digital_forest_description')} />
        <meta name="keywords" content="dijital orman, sürdürülebilirlik, çevre bilinci, Stoneity, digital forest, sustainability, environmental awareness" />
        <link key="canonical" rel="canonical" href={canonicalUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={t('seo_digital_forest_title')} />
        <meta property="og:description" content={t('seo_digital_forest_description')} />
        <meta property="og:image" content={`${baseUrl}/stoneityLogoGreen.png`} />
        <meta property="og:site_name" content="Stoneity" />
        <meta property="og:locale" content={locale === 'tr' ? 'tr_TR' : 'en_US'} />
        <meta property="og:locale:alternate" content={locale === 'tr' ? 'en_US' : 'tr_TR'} />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={canonicalUrl} />
        <meta name="twitter:title" content={t('seo_digital_forest_title')} />
        <meta name="twitter:description" content={t('seo_digital_forest_description')} />
        <meta name="twitter:image" content={`${baseUrl}/stoneityLogoGreen.png`} />
        <meta name="twitter:site" content="@stoneity" />

        {/* Alternate languages */}
        <link rel="alternate" hrefLang={locale} href={canonicalUrl} />
        <link rel="alternate" hrefLang={alternateLocale} href={`${baseUrl}/${alternateLocale}${alternatePath}`} />
        <link rel="alternate" hrefLang="x-default" href={`${baseUrl}${alternatePath}`} />
      </Head>
      <Header logo="green" theme="green" color='#13472E' />
      <div className={styles.mainFlex}>
        <div className={styles.digitalForestBanner}>
          <Image
            className={styles.digitalForestImg}
            src={`${IMAGE_DELIVERY}/939baa48-8b57-404f-506b-9baa6cbb9200`}
            alt=""
            width={1920}
            height={930}
            loader={(p: ImageLoaderProps) => imageLoader(p.src, 1920, 930)}
          />
          <div className={styles.digitalForestBannerTextWrapper}>
            <div className={styles.title}>{t('digitalForestBannerTitle')}</div>
            <div className={styles.text}>{t('digitalForestBannerText')}</div>
          </div>
        </div>
        <div className={styles.pageFlex}>
          <div className={styles.digitalForestRowWrapper}>
            <div className={styles.digitalForestRowTextWrapper}>
              <div className={styles.title}>{t('whatIsTheDigitalForestProject')}</div>
              <div className={styles.text}>{t('whatIsTheDigitalForestProjectText')}</div>
            </div>
            <Image
              className={styles.digitalForestRowCertificateImg}
              src={`${IMAGE_DELIVERY}/b9f9f5af-5b79-4f39-bb24-d8c3bc551400`}
              alt=""
              width={430}
              height={600}
              loader={(p: ImageLoaderProps) => imageLoader(p.src, 430, 600)}
            />
          </div>
        </div>
        <div className={styles.pageFlex}>
          <div className={styles.digitalForestSloganRowWrapper}>
            <Image
              className={styles.digitalForestRowImg}
              src={isLanguage === "TR" ? `${IMAGE_DELIVERY}/b348ba4a-9509-4926-4987-0d5258543200` : `${IMAGE_DELIVERY}/207d48a2-2557-4237-f66c-f8accab92200`}
              alt=""
              width={555}
              height={280}
              loader={(p: ImageLoaderProps) => imageLoader(p.src, 555, 280)}
            />
            <div className={styles.digitalForestRowTextWrapper}>
              <div className={styles.title}>{t('digitalForestProjectGoals')}</div>
              <div className={styles.text}>{t('digitalForestProjectGoalsText')}</div>
            </div>
          </div>
        </div>
      </div >
      <Footer logo="green" color='#13472E' />
    </>
  )
}

export const getStaticProps: GetStaticProps = async ({ locale }) => ({
  props: {
    ...(await serverSideTranslations(locale || i18nextConfig.i18n.defaultLocale, ['common']))
  },
});

export default DigitalForest;